// src/App.tsx
import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";

/* ===== 認可ガード ===== */
import RequireAuth from "./components/auth/RequireAuth";

/* ===== ユーザー側ページ ===== */
import LP from "./pages/LP";
import PlansList from "./pages/PlansList";
import PlanDetail from "./pages/PlanDetail";
import PlanPurchase from "./pages/PlanPurchase";
import Login from "./pages/Login";
import AuthLine from "./pages/AuthLine";
import PointsBuy from "./pages/PointsBuy";
import NoticesPage from "./pages/Notices";
import NoticeDetail from "./pages/NoticeDetail";
import MyPage from "./pages/MyPage";
import ResultsUser from "./pages/ResultsUser";
import Contact from "./pages/Contact";
import Affiliate from "./pages/Affiliate";
import AffiliateSalesPage from "./pages/AffiliateSales";

/* 法定 */
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Law from "./pages/Law";
import KyoteiLaw from "./pages/KyoteiLaw";
import Faq from "./pages/Faq";

/* レビュー（ユーザー側） */
import ReviewsPublic from "./pages/Reviews";
import ReviewNew from "./pages/ReviewNew";

/* 管理レイアウト＆画面 */
import AdminLayout from "./components/admin/AdminLayout";
import Dashboard from "./components/admin/Dashboard";
import Users from "./components/admin/Users";
import Notices from "./components/admin/Notices";
import Reviews from "./components/admin/Reviews";
import ReviewEdit from "./components/admin/ReviewEdit";
import Results from "./components/admin/Results";
import Sales from "./components/admin/Sales";
import Points from "./components/admin/Points";
import AffiliateList from "./components/admin/AffiliateList";
import AffiliateSales from "./components/admin/AffiliateSales";
import AffiliateLink from "./components/admin/AffiliateLink";
import Plans from "./components/admin/Plans";
import PlansCreate from "./components/admin/PlansCreate";
import Contacts from "./components/admin/Contacts";

/* Entry LP */
import EntryLP from "./pages/EntryLP";

/* 管理ログインページ */
import AdminLogin from "./components/admin/AdminLogin";

// AdminLogin と同じキー & 24時間タイムアウト
const ADMIN_LOGIN_STORAGE_KEY = "okm_admin_logged_in";
const ADMIN_LAST_ACCESS_KEY = "okm_admin_last_access";
const ADMIN_TIMEOUT_MS = 24 * 60 * 60 * 1000; // 24時間

/* =============================
   管理ログインガード（ID/PASS）
============================= */
const RequireAdminLogin: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [authed, setAuthed] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    try {
      const logged =
        localStorage.getItem(ADMIN_LOGIN_STORAGE_KEY) === "1";
      const last = localStorage.getItem(ADMIN_LAST_ACCESS_KEY);

      if (logged && last) {
        const diff = Date.now() - Number(last);
        if (!Number.isNaN(diff) && diff < ADMIN_TIMEOUT_MS) {
          // 24時間以内 → OK
          setAuthed(true);
          return;
        }
      }

      // タイムアウト or 未ログイン → クリア
      localStorage.removeItem(ADMIN_LOGIN_STORAGE_KEY);
      localStorage.removeItem(ADMIN_LAST_ACCESS_KEY);
      setAuthed(false);
    } catch {
      setAuthed(false);
    }
  }, []);

  // 判定中は何も描画しない
  if (authed === null) return null;

  if (!authed) {
    return <Navigate to="/admin/login" replace />;
  }
  return <>{children}</>;
};

/* =============================
   メインコンポーネント
============================= */
export default function App() {
  return (
    <Routes>
      {/* Entry LP（唯一の公開入口） */}
      <Route path="/entry" element={<EntryLP />} />

      {/* HOME（ログイン必須） */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <LP />
          </RequireAuth>
        }
      />

      {/* /lp → / （/ もログイン必須なので、結果的に未ログインは /entry へ） */}
      <Route path="/lp" element={<Navigate to="/" replace />} />

      {/* 認証系（ログイン処理用：公開） */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth/line" element={<AuthLine />} />

      {/* 保護ページ（ログイン必須） */}
      <Route
        path="/plans"
        element={
          <RequireAuth>
            <PlansList />
          </RequireAuth>
        }
      />
      <Route
        path="/plans/:id"
        element={
          <RequireAuth>
            <PlanDetail />
          </RequireAuth>
        }
      />
      <Route
        path="/plans/:id/purchase"
        element={
          <RequireAuth>
            <PlanPurchase />
          </RequireAuth>
        }
      />
      <Route
        path="/points-buy"
        element={
          <RequireAuth>
            <PointsBuy />
          </RequireAuth>
        }
      />
      <Route
        path="/affiliate"
        element={
          <RequireAuth>
            <Affiliate />
          </RequireAuth>
        }
      />
      <Route
        path="/affiliate/sales"
        element={
          <RequireAuth>
            <AffiliateSalesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/mypage"
        element={
          <RequireAuth>
            <MyPage />
          </RequireAuth>
        }
      />
      <Route
        path="/results"
        element={
          <RequireAuth>
            <ResultsUser />
          </RequireAuth>
        }
      />
      <Route
        path="/contact"
        element={
          <RequireAuth>
            <Contact />
          </RequireAuth>
        }
      />

      {/* Notices もログイン必須 */}
      <Route
        path="/notices"
        element={
          <RequireAuth>
            <NoticesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/notices/:id"
        element={
          <RequireAuth>
            <NoticeDetail />
          </RequireAuth>
        }
      />

      {/* レビュー一覧もログイン必須 */}
      <Route
        path="/reviews"
        element={
          <RequireAuth>
            <ReviewsPublic />
          </RequireAuth>
        }
      />
      <Route
        path="/reviews/new"
        element={
          <RequireAuth>
            <ReviewNew />
          </RequireAuth>
        }
      />
      <Route
        path="/reviews/new/:planId"
        element={
          <RequireAuth>
            <ReviewNew />
          </RequireAuth>
        }
      />

      {/* 法定ページもログイン必須 */}
      <Route
        path="/privacy"
        element={
          <RequireAuth>
            <Privacy />
          </RequireAuth>
        }
      />
      <Route
        path="/terms"
        element={
          <RequireAuth>
            <Terms />
          </RequireAuth>
        }
      />
      <Route
        path="/law"
        element={
          <RequireAuth>
            <Law />
          </RequireAuth>
        }
      />
      <Route
        path="/kyotei-law"
        element={
          <RequireAuth>
            <KyoteiLaw />
          </RequireAuth>
        }
      />
      <Route
        path="/faq"
        element={
          <RequireAuth>
            <Faq />
          </RequireAuth>
        }
      />

      {/* 管理ログインページ（公開） */}
      <Route path="/admin/login" element={<AdminLogin />} />

      {/* Admin（ID/PASS 必須） */}
      <Route
        path="/admin"
        element={
          <RequireAdminLogin>
            <AdminLayout />
          </RequireAdminLogin>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="users" element={<Users />} />
        <Route path="plans" element={<Plans />} />
        <Route path="plans/new" element={<PlansCreate />} />
        <Route path="plans/:id/edit" element={<PlansCreate />} />
        <Route path="notices" element={<Notices />} />

        {/* レビュー管理 */}
        <Route path="reviews" element={<Reviews />} />
        <Route path="reviews/edit/:id" element={<ReviewEdit />} />

        <Route path="results" element={<Results />} />
        <Route path="sales" element={<Sales />} />
        <Route path="points" element={<Points />} />
        <Route path="affiliate-list" element={<AffiliateList />} />
        <Route path="affiliate-sales" element={<AffiliateSales />} />
        <Route path="affiliate-link" element={<AffiliateLink />} />

        {/* お問い合わせ管理 */}
        <Route path="contacts" element={<Contacts />} />
        <Route
          path="contacts/:id"
          element={<Navigate to="/admin/contacts" replace />}
        />
        <Route
          path="messages"
          element={<Navigate to="/admin/contacts" replace />}
        />
        <Route
          path="messages/:id"
          element={<Navigate to="/admin/contacts" replace />}
        />
      </Route>

      {/* 想定外 → / （/ も RequireAuth 経由なので、未ログインは /entry に飛ばされる） */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
